/* eslint-disable no-void */

import * as vp from 'vopay';

import { useEffect, useState } from 'react';
import axios from 'axios';

import vopayApi from '../../services/api.config';
import { useDataFetchIndicator } from '..';

type HookProps = {
  api: vp.Api;
};

export type AccountStatementCsvRequest = {
  invoiceId: string;
};

export type AccountStatementCsvResponse = {
  success: boolean;
  errorMessage: string;
  statementCsv: string;
};

type ReturnTuple = [
  AccountStatementCsvResponse | undefined,
  (requestParams: AccountStatementCsvRequest) => void,
  () => void,
];

function useFetchAccountStatementCsv({ api }: HookProps): ReturnTuple {
  const source = axios.CancelToken.source();

  const [params, setParams] = useState<AccountStatementCsvRequest>();
  const [response, setResponse] = useState<AccountStatementCsvResponse>();
  const [shouldSendRequest, setSendRequest] = useState(false);
  const [, setDataFetchingInfo] = useDataFetchIndicator();

  useEffect(() => {
    const sendRequest = async (requestParams: AccountStatementCsvRequest) => {
      setDataFetchingInfo(true, 'creating csv', 'fullScreen');
      const { Success, ErrorMessage, InvoiceCSV } = await api.fetchAccountStatementCsv(
        buildParams(requestParams),
        source,
      );

      const res = {
        success: Success,
        errorMessage: ErrorMessage,
        statementCsv: InvoiceCSV,
      };

      setResponse(res);
      setSendRequest(false);
      setDataFetchingInfo(false);

      return () => {};
    };

    if (params && shouldSendRequest) void sendRequest(params);

    return () => {
      source.cancel();
    };
  }, [shouldSendRequest]);

  return [
    response,
    (requestParams: AccountStatementCsvRequest) => {
      setParams(requestParams);
      setSendRequest(true);
    },
    () => {
      setResponse(undefined);
      setSendRequest(false);
    },
  ];

  function buildParams(requestParams: AccountStatementCsvRequest): vp.StatementCsvRequest {
    return {
      InvoiceID: requestParams.invoiceId,
    };
  }
}

export default vopayApi.withApiHook<HookProps, ReturnTuple>(useFetchAccountStatementCsv);
